import { useEffect, useMemo, useRef } from 'react';

import L from 'leaflet';
import { useMap } from 'react-leaflet';

import { isValidLatLngTuple } from '../../utils/parseCoordinates';

const DEFAULT_ZOOM = 16;
const RESIZE_DELAYS_MS = [0, 100, 300];

/**
 * Programmatic fits must not animate: Leaflet schedules a zoom-transition-end
 * callback that still runs after `map.remove()` (unmount) and then reads
 * `_leaflet_pos` of the already-deleted map pane, throwing a TypeError.
 */
const FIT_OPTIONS: L.FitBoundsOptions = { padding: [40, 40], animate: false };

type TFitBoundsCircle = {
  position: [number, number];
  radius: number;
};

type TFitBoundsProps = {
  positions: [number, number][];
  zoom?: number;
  circles?: TFitBoundsCircle[];
};

const canFitMap = (map: L.Map) => {
  const container = map.getContainer();
  return container.offsetWidth > 0 && container.offsetHeight > 0;
};

const fitMapToPositions = (
  map: L.Map,
  positions: [number, number][],
  zoom: number,
  circles: TFitBoundsCircle[],
) => {
  if ((!positions.length && !circles.length) || !canFitMap(map)) {
    return false;
  }

  map.invalidateSize();

  const validCircles = circles.filter(
    circle => isValidLatLngTuple(circle.position) && circle.radius > 0,
  );

  if (validCircles.length > 0) {
    const bounds = L.latLngBounds(validCircles[0].position, validCircles[0].position);
    validCircles.forEach(circle => {
      bounds.extend(L.latLng(circle.position[0], circle.position[1]).toBounds(circle.radius));
    });
    positions.forEach(position => {
      if (isValidLatLngTuple(position)) {
        bounds.extend(position);
      }
    });
    map.fitBounds(bounds, FIT_OPTIONS);
    return true;
  }

  if (positions.length === 1) {
    map.setView(positions[0], zoom, { animate: false });
    return true;
  }

  map.fitBounds(L.latLngBounds(positions), FIT_OPTIONS);
  return true;
};

export const FitBounds = ({ positions, zoom = DEFAULT_ZOOM, circles = [] }: TFitBoundsProps) => {
  const map = useMap();
  const lastFittedKeyRef = useRef<string | null>(null);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const validPositions = useMemo(
    () => positions.filter(position => isValidLatLngTuple(position)),
    [positions],
  );

  const fitKey = useMemo(() => {
    const positionsPart = validPositions.map(position => position.join('|')).join(',');
    const circlesPart = circles
      .map(circle => `${circle.position.join('|')}:${circle.radius}`)
      .join(',');
    return `${positionsPart}#${circlesPart}`;
  }, [circles, validPositions]);

  useEffect(() => {
    timeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutIdsRef.current = [];

    if (!validPositions.length) {
      lastFittedKeyRef.current = null;
      return undefined;
    }

    if (fitKey === lastFittedKeyRef.current) {
      return undefined;
    }

    const tryFit = () => {
      if (!fitMapToPositions(map, validPositions, zoom, circles)) {
        return false;
      }

      lastFittedKeyRef.current = fitKey;
      return true;
    };

    const scheduleFitAttempts = () => {
      RESIZE_DELAYS_MS.forEach(delay => {
        const timeoutId = setTimeout(() => {
          tryFit();
        }, delay);
        timeoutIdsRef.current.push(timeoutId);
      });
    };

    map.whenReady(scheduleFitAttempts);

    return () => {
      timeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutIdsRef.current = [];
    };
  }, [circles, fitKey, map, validPositions, zoom]);

  return null;
};
